import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  User,
  UserManager,
  UserManagerEvents,
  WebStorageStateStore,
} from "oidc-client";
import { BehaviorSubject, Observable, Subject, from, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { LocalSettingsService } from "~app/core/storage";
import { environment } from "~env";
import { AuthUser } from "../core/auth-user.model";
import { AuthService } from "../core/auth.service";

@Injectable()
export class OidcClientAuthService implements AuthService, OnDestroy {
  private userState = new BehaviorSubject<AuthUser>(null);
  userState$ = this.userState.asObservable();

  private errors = new Subject<void>();
  errors$ = this.errors.asObservable();

  private loginChanged = new BehaviorSubject<boolean>(false);
  loginChanged$ = this.loginChanged.asObservable();

  private tokenExpiring = new Subject<number>();
  tokenExpiring$ = this.tokenExpiring.asObservable();

  private userManager: UserManager;
  private userManagerPasswordReset: UserManager;
  private user: User;
  private currentLoginChanged = false;
  private oidcResetPasswordSetting = "oidc.settings.isResetPassword";

  private destroy$ = new Subject<void>();
  constructor(
    private translate: TranslateService,
    private localSettingsService: LocalSettingsService,
    private router: Router
  ) {
    const stsSettings = {
      authority: environment.identity.authority,
      client_id: environment.identity.clientId,
      redirect_uri: `${location.origin}${environment.identity.redirectUri}`,
      scope: environment.identity.consentScopes.join(" "),
      post_logout_redirect_uri: `${location.origin}${environment.identity.postLogoutRedirectUri}`,
    };
    const stsSettingsResetPassword = {
      authority: environment.identity.authorityResetPassword,
      client_id: environment.identity.clientId,
      redirect_uri: `${location.origin}${environment.identity.redirectUri}`,
      scope: environment.identity.consentScopes.join(" "),
      post_logout_redirect_uri: `${location.origin}${environment.identity.postLogoutRedirectUri}`,
    };

    const globalSettings = {
      response_type: "code",
      response_mode: "fragment",
      loadUserInfo: false,
      automaticSilentRenew: false,
      monitorSession: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      revokeAccessTokenOnSignout: true,
      accessTokenExpiringNotificationTime: 240,
      clockSkew: 900,
    };

    this.userManager = this.createUserManager(stsSettings, globalSettings);
    this.userManagerPasswordReset = this.createUserManager(
      stsSettingsResetPassword,
      globalSettings
    );

    // Settings are stored into session storage so they can be accessed from other HTML files (e.g. silent renew).
    sessionStorage.setItem("oidc.settings", JSON.stringify(stsSettings));
    this.assignUser(null);
  }

  ngOnDestroy() {
    this.setIsResetPassword(false);

    this.destroyUserManager(this.userManager);
    this.destroyUserManager(this.userManagerPasswordReset);

    this.userState.complete();
    this.errors.complete();
    this.loginChanged.complete();
    this.tokenExpiring.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }

  signIn(returnPath?: string): void {
    this.userState.next(null);
    this.errors.next(null);
    this.setIsResetPassword(false);
    this.userManager.signinRedirect({ state: returnPath });
  }

  signOut(): void {
    console.log(`sognOut from oidc-client-auth.service.ts : ${this.user}`);
    console.log(this.user);

    this.localSettingsService.clearBrowserData();

    if (this.user) {
      this.setIsResetPassword(false);
      this.userManager.signoutRedirect().then((_) => {
        this.userManager.clearStaleState();
      });
    }
  }

  // New user flow to reset password using email verification and finish logged in with proper auth token
  resetPassword(returnPath?: string): void {
    this.userState.next(null);
    this.errors.next(null);

    this.setIsResetPassword(true);
    this.userManagerPasswordReset.signinRedirect({ state: returnPath });
  }

  isLoggedIn(): Observable<boolean> {
    const user$ = from(this.GetProperUserManager().getUser());

    return user$.pipe(
      map((user) => {
        const userCurrent =
          !!user && user.expired !== undefined && !user.expired;
        this.assignUser(user);
        return userCurrent;
      }),
      catchError((err) => {
        console.error("oidc-client isLoggedIn error: ", err);
        this.assignUser(null);
        return of(false);
      })
    );
  }

  completeLogin() {
    return this.GetProperUserManager()
      .signinRedirectCallback()
      .then((user) => {
        this.assignUser(user);
        return user.state;
      })
      .catch((err) => {
        console.error("signin-redirect-callback error: ", err);
        this.assignUser(null);

        // si err.Error === 'No state in response', la session est terminé
        // TODO:sg La traduction ne fonctionne pas dans une Promise, ou un prob de life cycle
        this.errors.next(
          this.translate.instant("error.auth.no_authentication_service")
        );
      });
  }

  onUserLoaded(
    self: OidcClientAuthService
  ): UserManagerEvents.UserLoadedCallback {
    return (user: User) => {
      this.assignUser(user);
    };
  }

  onUserSessionChanged(
    self: OidcClientAuthService
  ): UserManagerEvents.UserSessionChangedCallback {
    return () => {};
  }

  onAccessTokenExpired(self: OidcClientAuthService): (...ev: any[]) => void {
    return (...ev: any[]) => {
      this.assignUser(null);
      this.tokenExpiring.next(0);
      this.signOut();
      this.router.navigate(["home"]);
    };
  }

  onAccessTokenExpiring(self: OidcClientAuthService): (...ev: any[]) => void {
    return (...ev: any[]) => {
      this.tokenExpiring.next(this.user.expires_at);
    };
  }

  onSilentRenewError(
    self: OidcClientAuthService
  ): UserManagerEvents.SilentRenewErrorCallback {
    return (error) => {
      console.error("SilentRenewError!!!!!!!!!", error);
    };
  }

  completeLogout() {
    console.log("Complete logout Oidc-Client");
    this.assignUser(null);
    return this.GetProperUserManager().signoutRedirectCallback();
  }

  completeSilent() {
    return this.GetProperUserManager()
      .signinSilentCallback()
      .then((user) => {
        this.assignUser(user);
        return user;
      })
      .catch((err) => {
        console.error("signin-silent-callback error: ", err);
        this.assignUser(null);

        // si err.Error === 'No state in response', la session est terminé
        // TODO:sg La traduction ne fonctionne pas dans une Promise, ou un prob de life cycle
        this.errors.next(
          this.translate.instant("error.auth.no_authentication_service")
        );
      });
  }

  async getAccessToken(): Promise<string> {
    const user = await this.GetProperUserManager().getUser();
    return !!user && user.expired !== undefined && !user.expired
      ? user.access_token
      : null;
  }

  getAccessTokenSync(): string {
    return this.user.access_token;
  }

  removeUser() {
    this.GetProperUserManager()
      .removeUser()
      .then((_) => this.assignUser(null))
      .catch((err) => console.error("Unable to removeUser!!!", err));
  }

  // Creates a userManager with proper settings for differnent user flows (ie: signin vs reset Password)
  private createUserManager(stsSettings, globalSettings) {
    let userManager: UserManager;
    userManager = new UserManager({ ...stsSettings, ...globalSettings });

    userManager.events.addUserLoaded(this.onUserLoaded(this));
    userManager.events.addUserSessionChanged(this.onUserSessionChanged(this));
    userManager.events.addAccessTokenExpired(this.onAccessTokenExpired(this));
    userManager.events.addSilentRenewError(this.onSilentRenewError(this));
    userManager.events.addAccessTokenExpiring(this.onAccessTokenExpiring(this));
    return userManager;
  }

  // Destroys event registrations for a userManager
  private destroyUserManager(userManager: UserManager) {
    userManager.events.removeUserLoaded(this.onUserLoaded(this));
    userManager.events.removeUserSessionChanged(
      this.onUserSessionChanged(this)
    );
    userManager.events.removeAccessTokenExpired(
      this.onAccessTokenExpired(this)
    );
    userManager.events.removeAccessTokenExpiring(
      this.onAccessTokenExpiring(this)
    );
    userManager.events.removeSilentRenewError(this.onSilentRenewError(this));
  }

  private assignUser(user: User) {
    const isUserLoggedIn =
      !!user && user.expired !== undefined && !user.expired;
    if (isUserLoggedIn) {
      if (this.user !== user) {
        const authUser = new AuthUser();
        authUser.id = user.profile.sub;
        authUser.name = user.profile.name;
        authUser.roles = user.profile.roles || ["portalGuest"];
        authUser.permitNumber = user.profile.permitNumber;
        authUser.email = user.profile.email;

        this.user = user;
        this.userState.next(authUser);
      }
    } else {
      this.user = null;
      this.userState.next(null);
    }

    if (isUserLoggedIn !== this.currentLoginChanged) {
      this.currentLoginChanged = isUserLoggedIn;
      this.loginChanged.next(isUserLoggedIn);
    }
  }

  // Set flag to tell callback functions wich userManager to use!!
  private setIsResetPassword(isPasswordReset: boolean) {
    if (isPasswordReset) {
      // Set flag to tell callback to use proper userManager
      sessionStorage.setItem(this.oidcResetPasswordSetting, "true");
    } else {
      sessionStorage.removeItem(this.oidcResetPasswordSetting);
    }
  }

  // Returns proper userManager to use based on initial user flow (resetPassword or others)
  private GetProperUserManager(): UserManager {
    let userManager: UserManager;
    if (sessionStorage.getItem(this.oidcResetPasswordSetting)) {
      userManager = this.userManagerPasswordReset;
    } else {
      userManager = this.userManager;
    }
    return userManager;
  }
}
